import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FilePlus2, FileSpreadsheet, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Scaffold } from 'layouts';
import { DataTableV2, type DataTableV2Ref } from 'components/ui/DataTableV2';
import { PageHeaderForFilters } from 'components/ui/page-header-for-filters';
import { Button } from 'components/ui/ButtonGs';
import { notify } from 'services/notifications';
import { useViagensBilling } from 'hooks/api/viagens-billing';
import { useCreateScheduledReport } from 'hooks/api/scheduled-reports';
import { useViagensBillingSearchParams } from 'hooks/store/viagens-billing';
import { SCHEDULED_REPORTS_ROUTES, VIAGENS_CONTROLE_ROUTES } from 'lib/routes';
import type { ViagemControle, ViagensBillingViagensFilters } from '../types';
import { PageTitle } from '../components/page-title';
import { TabbedNavigation } from '../components/tabbed-navigation';
import { ViagemControleActivitiesDialog } from '../viagem-controle-activities-dialog';
import { ViagensBillingViagensFilter } from './components/viagens-billing-viagens-filter';
import { useViagensToBillingColumns } from './hooks';
import {
  formatViagemCurrency,
  isViagemEligibleForBilling,
  sumViagensEstadia,
} from '../utils';

const QUERY_KEY = 'fetch-billable-viagens';

export const ViagensBillingCreatePage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tableRef = React.useRef<DataTableV2Ref>(null);
  const [activitiesViagem, setActivitiesViagem] =
    React.useState<ViagemControle | null>(null);
  const [filterLoadingAction, setFilterLoadingAction] = React.useState<
    'filter' | 'reset' | null
  >(null);
  const { fetchBillableViagens, createBilling } = useViagensBilling();
  const { createScheduledReport } = useCreateScheduledReport();
  const {
    viagensFilters,
    viagensPaginationControl,
    updateViagensFilters,
    updateViagensPaginationControl,
    clearViagensState,
  } = useViagensBillingSearchParams({ persisted: true });

  const viagensQuery = useQuery({
    queryKey: [QUERY_KEY, viagensFilters, viagensPaginationControl],
    queryFn: () =>
      fetchBillableViagens({
        filters: viagensFilters,
        pagination: viagensPaginationControl,
      }),
    refetchOnWindowFocus: false,
  });

  const createMutation = useMutation({
    mutationKey: ['create-viagens-billing'],
    mutationFn: createBilling,
    onSuccess: (billingId) => {
      notify('success', `Fatura ${billingId} criada com sucesso.`);
      tableRef.current?.clearSelection();
      queryClient.invalidateQueries(QUERY_KEY);
      queryClient.invalidateQueries('fetch-paginated-viagens-billings');
      navigate(VIAGENS_CONTROLE_ROUTES.billings().url);
    },
    onError: (error: any) => notify('error', error?.message),
  });

  const createReportMutation = useMutation({
    mutationKey: ['create-scheduled-viagens-billing-report'],
    mutationFn: createScheduledReport,
    onSuccess: () => {
      notify('success', 'Relatório requisitado com sucesso.');
      navigate(SCHEDULED_REPORTS_ROUTES.viagensBilling().url);
    },
    onError: (error: any) => notify('error', error?.message),
  });

  const handleFilters = React.useCallback(
    (nextFilters: ViagensBillingViagensFilters) => {
      setFilterLoadingAction('filter');
      updateViagensFilters(nextFilters);
      updateViagensPaginationControl({ page: 1 });
    },
    [updateViagensFilters, updateViagensPaginationControl]
  );

  const handleResetFilters = React.useCallback(() => {
    setFilterLoadingAction('reset');
    clearViagensState();
  }, [clearViagensState]);

  const handleCreateBilling = React.useCallback(
    (selectedRows: ViagemControle[]) => {
      if (!selectedRows.length) {
        notify('error', 'Selecione ao menos uma viagem para faturar.');
        return;
      }

      const invalidRows = selectedRows.filter(
        (row) => !isViagemEligibleForBilling(row)
      );

      if (invalidRows.length) {
        notify(
          'error',
          'Apenas viagens sem fatura ou vinculadas a faturas reprovadas/estornadas podem gerar fatura.'
        );
        return;
      }

      const carrierIds = [
        ...new Set(selectedRows.map((row) => row.carrierId).filter(Boolean)),
      ];

      if (carrierIds.length > 1) {
        notify(
          'error',
          'Não é possível criar uma fatura com transportadoras diferentes.'
        );
        return;
      }

      createMutation.mutate({
        viagemIds: selectedRows.map((row) => row.id),
        totalValue: sumViagensEstadia(selectedRows),
      });
    },
    [createMutation]
  );

  const handleRequestReport = React.useCallback(() => {
    if (!viagensQuery.data?.meta?.total) {
      notify('error', 'Não existem viagens para exportar.');
      return;
    }

    createReportMutation.mutate({
      type: 'viagens-billing',
      params: { ...viagensFilters },
    });
  }, [createReportMutation, viagensFilters, viagensQuery.data?.meta?.total]);

  const columns = useViagensToBillingColumns({
    onShowActivities: setActivitiesViagem,
  });
  const eligibleViagens = React.useMemo(() => {
    return (viagensQuery.data?.data ?? []).filter(isViagemEligibleForBilling);
  }, [viagensQuery.data?.data]);

  React.useEffect(() => {
    if (filterLoadingAction && !viagensQuery.isFetching) {
      setFilterLoadingAction(null);
    }
  }, [filterLoadingAction, viagensQuery.isFetching]);

  return (
    <Scaffold>
      <TabbedNavigation />
      <div className="flex flex-col gap-6">
        <PageHeaderForFilters
          headerTitle={<PageTitle subtitle="Gerar fatura" />}
        >
          <ViagensBillingViagensFilter
            onSubmitFilters={handleFilters}
            onResetFilters={handleResetFilters}
            filterLoadingAction={filterLoadingAction}
          />
        </PageHeaderForFilters>

        <DataTableV2
          ref={tableRef}
          data={eligibleViagens}
          columns={columns}
          initialColumnVisibility={{ idViagem: false }}
          manualPagination
          showControlColumns
          noBreakHeader
          onPaginationChange={updateViagensPaginationControl}
          isLoading={viagensQuery.isLoading}
          tableWrapperClassName="max-h-[calc(100vh-330px)]"
          renderTopBar={({ selectedRows }) => (
            <div className="flex flex-wrap items-center gap-2">
              <Button.Root
                type="button"
                size="sm"
                disabled={!selectedRows.length || createMutation.isLoading}
                onClick={() => handleCreateBilling(selectedRows)}
              >
                Gerar fatura ({selectedRows.length})
                <Button.Icon>
                  {createMutation.isLoading ? (
                    <Loader className="h-4 w-4 animate-spin" />
                  ) : (
                    <FilePlus2 className="h-4 w-4" />
                  )}
                </Button.Icon>
              </Button.Root>
              <Button.Root
                type="button"
                size="sm"
                variant="secondary"
                disabled={
                  createReportMutation.isLoading ||
                  viagensQuery.isLoading ||
                  !viagensQuery.data?.meta?.total
                }
                onClick={handleRequestReport}
              >
                Exportar
                <Button.Icon>
                  {createReportMutation.isLoading ? (
                    <Loader className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="h-4 w-4" />
                  )}
                </Button.Icon>
              </Button.Root>
              <span className="whitespace-nowrap text-sm text-muted-foreground">
                Total selecionado:{' '}
                <strong className="text-foreground">
                  {formatViagemCurrency(sumViagensEstadia(selectedRows))}
                </strong>
              </span>
            </div>
          )}
          pagination={{
            ...viagensQuery.data?.meta,
            currentPage: viagensPaginationControl.page,
            perPage: viagensPaginationControl.limit,
          }}
          stickyHeader={true}
          rightPinnedColumns={['acoes']}
        />
        <ViagemControleActivitiesDialog
          viagem={activitiesViagem}
          onOpenChange={(open) => !open && setActivitiesViagem(null)}
        />
      </div>
    </Scaffold>
  );
};
